/**
 * An amount, in EUR, as of a date.
 *
 * WHY THIS IS IN THE SHARED KERNEL
 *
 * The dated-value-snapshot pattern appears three times in the product: savings balance snapshots,
 * stock price snapshots, and property valuations. All three are "an amount, at a date, for a
 * thing", and all three need the same "latest value as of date" lookup.
 *
 * This lives here, written before any of those three contexts exists, specifically so it cannot be
 * written three subtly different ways — with three different rounding rules and three different
 * answers to what happens when two snapshots share a date.
 *
 * GLADIS is EUR-only (docs/project-overview/), so there is no currency field. Amounts are held in
 * integer CENTS: `numeric(15,2)` in Postgres, and floating point is never used for money.
 */
export class Valuation {
  private constructor(
    private readonly cents: bigint,
    private readonly at: Date,
  ) {}

  static fromCents(cents: bigint | number, asOf: Date): Valuation {
    return new Valuation(typeof cents === "bigint" ? cents : BigInt(Math.round(cents)), asOf);
  }

  /** From a `numeric(15,2)` string as Postgres returns it, e.g. "1234.56". */
  static fromDecimalString(value: string, asOf: Date): Valuation {
    const [whole = "0", fraction = ""] = value.trim().split(".");
    const negative = whole.startsWith("-");
    const wholeDigits = negative ? whole.slice(1) : whole;
    const fractionDigits = `${fraction}00`.slice(0, 2);
    const magnitude = BigInt(wholeDigits || "0") * 100n + BigInt(fractionDigits);
    return new Valuation(negative ? -magnitude : magnitude, asOf);
  }

  static zero(asOf: Date): Valuation {
    return new Valuation(0n, asOf);
  }

  get amountInCents(): bigint {
    return this.cents;
  }

  get asOf(): Date {
    return new Date(this.at.getTime());
  }

  /** For a `numeric(15,2)` column. Never a float. */
  toDecimalString(): string {
    const negative = this.cents < 0n;
    const magnitude = negative ? -this.cents : this.cents;
    const whole = magnitude / 100n;
    const fraction = (magnitude % 100n).toString().padStart(2, "0");
    return `${negative ? "-" : ""}${whole.toString()}.${fraction}`;
  }

  /**
   * Euros as a number, for `formatEur` and for the wire.
   *
   * The ONE place cents become a float, for the same reason `formatEur` is the one place an amount
   * becomes a string: `Number(cents) / 100` scattered across five modules is five chances to write
   * `/ 1000` once and not notice. Feature 006 added this when the savings DTO needed it; every
   * later pillar has the same need.
   *
   * Safe for a single stored amount — `numeric(15,2)` tops out at 9,999,999,999,999.99, where the
   * gap between representable doubles is about 0.002, well under a cent. It is NOT safe as a way to
   * add amounts up: do that with `plus`, in cents, and convert once at the end.
   */
  toEuros(): number {
    return Number(this.cents) / 100;
  }

  plus(other: Valuation): Valuation {
    const at = this.at >= other.at ? this.at : other.at;
    return new Valuation(this.cents + other.cents, at);
  }

  minus(other: Valuation): Valuation {
    const at = this.at >= other.at ? this.at : other.at;
    return new Valuation(this.cents - other.cents, at);
  }

  /**
   * This amount scaled by `numerator / denominator`, rounded to the cent, half away from zero.
   *
   * WHY THIS OPERATION AND NOT A GENERAL ARITHMETIC FACILITY
   *
   * Added by feature 009, whose every monetary quantity turns out to be an exact cent amount scaled by
   * a ratio of two small integers:
   *
   *   weekly → monthly        52 / 12
   *   every four weeks        13 / 12
   *   quarterly                1 / 3
   *   half-yearly              1 / 6
   *   yearly                   1 / 12
   *   a split share, 2 dp     basis points / 10000
   *   an equal share of n      1 / n
   *
   * `cents * numerator` is exact integer arithmetic, and the single division is the ONLY rounding that
   * feature performs — every figure its page shows passes through here once. That is what lets a
   * displayed total equal the sum of the displayed rows, and it is what makes the monthly settle-up
   * reproducible by hand from the figures printed beside it.
   *
   * It is deliberately NOT a fixed-point library. `@gladis/domain-properties` needs `(1 + r)^n` for an
   * annuity factor, which is not a ratio of small integers, and it keeps its own 20-digit `fixed-point`
   * module private for a reason its header gives: that module's correctness rests on the two functions
   * tested against an independently produced amortisation table, and a second caller would inherit the
   * code without the guarantee. Nothing here needs exponentiation, so nothing here reaches for it.
   *
   * ROUNDING IS SYMMETRIC — half AWAY from zero, matching `fixed-point.toCentsHalfUp` so the two
   * pillars round the same way. Every amount feature 009 scales is positive, where away-from-zero and
   * half-up coincide; the rule is stated for sign anyway, because a rounding function whose behaviour
   * depends on sign is a trap for whoever reuses it.
   *
   * A zero `denominator` is an unrecoverable programmer error and THROWS rather than returning a
   * failure (Principle V). It is unreachable by construction: every caller passes a literal from the
   * table above, or a member count already known to be at least one. A `Result` here would put an
   * error branch in the arithmetic of every row on the page to model a case no input can produce.
   */
  scaledBy(numerator: bigint, denominator: bigint): Valuation {
    if (denominator === 0n) {
      throw new Error("Valuation.scaledBy: denominator must not be zero.");
    }

    const scaled = this.cents * numerator;

    // Work on magnitudes so the half-way comparison is one expression rather than one per sign.
    const negative = scaled < 0n !== denominator < 0n;
    const magnitude = scaled < 0n ? -scaled : scaled;
    const divisor = denominator < 0n ? -denominator : denominator;

    const quotient = magnitude / divisor;
    const remainder = magnitude % divisor;
    // `remainder * 2n >= divisor` rather than `remainder / divisor >= 0.5`: comparing doubled
    // integers is exact, and there is no float in the expression to be a hair under the boundary.
    const rounded = remainder * 2n >= divisor ? quotient + 1n : quotient;

    return new Valuation(negative ? -rounded : rounded, this.at);
  }

  equals(other: Valuation): boolean {
    return this.cents === other.cents && this.at.getTime() === other.at.getTime();
  }
}

export interface DatedValue {
  asOf: Date;
  valuation: Valuation;
}

/**
 * The latest snapshot at or before `on`, or undefined if none exists.
 *
 * Ties are broken by taking the LAST entry among equal dates, which makes the answer depend on
 * input order — so callers pass rows ordered by the database's own tiebreak (recorded_at, then id).
 * Stating it here means three contexts do not each invent their own answer.
 */
export function latestAsOf<T extends DatedValue>(values: readonly T[], on: Date): T | undefined {
  let best: T | undefined;
  for (const value of values) {
    if (value.asOf.getTime() <= on.getTime()) {
      if (!best || value.asOf.getTime() >= best.asOf.getTime()) {
        best = value;
      }
    }
  }
  return best;
}
